import { DataPacket_Kind, Room, RoomEvent } from "livekit-client";
import { useEffect, useRef, useState } from "react";

export default function ChatbotUI() {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const roomRef = useRef(null);
  const messagesEndRef = useRef(null);
  const conversationIdRef = useRef(null);

  // Fetch available models on mount
  useEffect(() => {
    async function fetchModels() {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL || "https://backend.modeai.io";
        const token = localStorage.getItem("accessToken") || "";
        const res = await fetch(`${backend}/ai/models/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          console.log("📊 Fetched models:", data);
          const textModels = data.text_models || [];
          console.log("📝 Text models:", textModels);
          setAvailableModels(textModels);
          if (textModels.length > 0) {
            setSelectedModel(textModels[0].model_name);
            console.log("✅ Selected default model:", textModels[0].model_name);
          }
        } else {
          console.error("❌ Failed to fetch models, status:", res.status);
        }
      } catch (e) {
        console.error("Failed to fetch models:", e);
      }
    }
    fetchModels();
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle model change
  const handleModelChange = (newModel) => {
    setSelectedModel(newModel);
  };

  // Manual connect function
  const handleConnect = async () => {
    if (!selectedModel || connecting) return;

    setConnecting(true);

    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "https://backend.modeai.io";
      const endpoint = `${backend}/chat/session/start/`;
      const token = localStorage.getItem("accessToken") || "";

      console.log("🎯 Connecting with model:", selectedModel);

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ model: selectedModel }),
      });

      if (!res.ok) {
        console.error("❌ Session start failed");
        setConnecting(false);
        return;
      }

      const data = await res.json();
      console.log("✅ Session started:", data);

      if (data.conversation_id) {
        conversationIdRef.current = data.conversation_id;
      }

      const { url, token: livekitToken } = data.livekit || {};

      if (!url || !livekitToken) {
        console.error("❌ Missing LiveKit credentials");
        setConnecting(false);
        return;
      }

      const room = new Room();
      roomRef.current = room;

      room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
        if (topic !== "chat:text" && topic !== "chat") return;

        let decoded;
        try {
          decoded = JSON.parse(new TextDecoder().decode(payload));
        } catch (e) {
          console.error("Failed to decode payload:", e);
          return;
        }

        if (decoded.type === "assistant_delta") {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === "assistant") {
              return [...prev.slice(0, -1), { ...last, text: last.text + decoded.delta }];
            }
            return [...prev, { role: "assistant", text: decoded.delta }];
          });
        } else if (decoded.type === "assistant_final") {
          console.log("📩 Received WebSocket Payload:", decoded);
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            const updated = {
              role: "assistant",
              text: decoded.text,
              model_display_name: decoded.model_display_name,
            };
            if (last?.role === "assistant") {
              return [...prev.slice(0, -1), updated];
            }
            return [...prev, updated];
          });
        } else if (decoded.role && decoded.text) {
          setMessages((prev) => [...prev, { role: decoded.role, text: decoded.text }]);
        }
      });

      room.on(RoomEvent.Connected, () => {
        console.log("✅ LiveKit Connected");
        setConnected(true);
        setConnecting(false);
      });

      room.on(RoomEvent.Disconnected, () => {
        console.log("❌ LiveKit Disconnected");
        setConnected(false);
      });

      setConnecting(true);
      console.log("📡 Attempting LiveKit connection to:", url);

      await room.connect(url, livekitToken);
    } catch (e) {
      console.error("🔥 Connect error:", e);
      setConnecting(false);
    }
  };

  const handleDisconnect = () => {
    try {
      roomRef.current?.disconnect();
    } catch (e) {
      console.error("Disconnect error:", e);
    }
    roomRef.current = null;
    conversationIdRef.current = null;
    setConnected(false);
    setConnecting(false);
    setMessages([]);
  };

  const handleSend = async () => {
    const text = message.trim();
    if (!text || !roomRef.current?.localParticipant) return;

    // 1. Update UI locally
    setMessages((prev) => [...prev, { role: "user", text }]);
    setMessage("");

    // 2. Save to backend via REST API immediately
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "https://backend.modeai.io";
      const token = localStorage.getItem("accessToken") || "";

      await fetch(`${backend}/chat/message/save/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversationIdRef.current,
          role: "user",
          content: text,
          selected_model: selectedModel,
        }),
      });
      console.log("✅ Message saved to DB via REST");
    } catch (e) {
      console.error("❌ Failed to save message to DB:", e);
    }

    // 3. Publish to LiveKit for Agent processing
    const payloadObj = { type: "user_msg", text, selected_model: selectedModel };
    const payload = new TextEncoder().encode(JSON.stringify(payloadObj));

    try {
      roomRef.current.localParticipant.publishData(
        payload,
        {
          reliable: true,
          topic: "chat:text"
        }
      );
    } catch (e) {
      console.error("❌ publishData failed:", e);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <div style={styles.statusDot(connected)} />
          <span style={styles.statusText}>
            {connecting ? "Connecting..." : connected ? "Connected" : "Disconnected"}
          </span>

          <select
            value={selectedModel}
            onChange={(e) => handleModelChange(e.target.value)}
            style={styles.modelSelector}
            disabled={availableModels.length === 0}
          >
            {availableModels.map((model) => (
              <option key={model.id} value={model.model_name}>
                {model.display_name || model.model_name}
              </option>
            ))}
          </select>

          {!connected && !connecting && (
            <button onClick={handleConnect} style={styles.connectButton} disabled={!selectedModel}>
              Connect
            </button>
          )}

          {connected && (
            <button onClick={handleDisconnect} style={styles.disconnectButton}>
              Disconnect
            </button>
          )}
        </div>

        <h3 style={{ marginBottom: 10 }}>Chat Assistant</h3>

        <div style={styles.messages}>
          {messages.map((m, i) => (
            <div key={i} style={m.role === "user" ? styles.userMessage : styles.assistantMessage}>
              <div style={{ fontSize: "0.75rem", color: "#6b7280", marginBottom: "2px" }}>
                {m.role === "user" ? "You" : `Assistant (${m.model_display_name || "AI"})`}
              </div>
              {m.text}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div style={styles.chatBox}>
          <input
            type="text"
            placeholder={connected ? "Type your message..." : "Select model and connect first"}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={styles.input}
            disabled={!connected}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
          />
          <button
            onClick={handleSend}
            style={{
              ...styles.button,
              ...(!connected || !message.trim() ? styles.buttonDisabled : {}),
            }}
            disabled={!connected || !message.trim()}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#f3f4f6",
    fontFamily: "Arial, sans-serif",
  },
  wrapper: {
    width: "520px",
    backgroundColor: "#fff",
    borderRadius: "10px",
    padding: "15px",
    boxShadow: "0 4px 25px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },
  statusDot: (on) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: on ? "#10b981" : "#f59e0b",
    boxShadow: on ? "0 0 4px #10b981" : "none",
  }),
  statusText: {
    fontSize: 12,
    color: "#374151",
  },
  modelSelector: {
    marginLeft: "auto",
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "13px",
    backgroundColor: "#fff",
    cursor: "pointer",
    outline: "none",
  },
  connectButton: {
    padding: "6px 12px",
    backgroundColor: "#10b981",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    cursor: "pointer",
    marginLeft: "8px",
  },
  disconnectButton: {
    padding: "6px 12px",
    backgroundColor: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    cursor: "pointer",
    marginLeft: "8px",
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    marginBottom: "10px",
    maxHeight: "400px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  userMessage: {
    alignSelf: "flex-end",
    backgroundColor: "#dbeafe",
    padding: "8px 12px",
    borderRadius: "8px",
  },
  assistantMessage: {
    alignSelf: "flex-start",
    backgroundColor: "#f3f4f6",
    padding: "8px 12px",
    borderRadius: "8px",
  },
  chatBox: {
    display: "flex",
    gap: "10px",
  },
  input: {
    flex: 1,
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "16px",
  },
  button: {
    padding: "10px 20px",
    backgroundColor: "#3b82f6",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    transition: "opacity .2s",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
};
